import axiosInstance from '../../constants/axiosInstance';

interface PlantListAutoCompleteProps {
  workspaceId?: string;
  plant: string;
}



export const fetchTriggers = async ({
  pageNumber,
  limit,
  searchValue,
  userGroup,
  asset,
}: {
  pageNumber: number;
  limit: number;
  searchValue: string;
  userGroup?: string;
  asset?: string;
}) => {
  const params = {
    pageNumber,
    limit,
    search: searchValue,
    userGroup,
    asset,
  };

  const response = await axiosInstance().get(`/trigger/getTriggers`, { params });
  const payload = response?.data?.data ?? {};
  const triggers = Array.isArray(payload.triggers) ? payload.triggers : [];

  const listOfTriggers = triggers.map((trigger: any) => ({
    id: trigger?._id,
    ...trigger,
  }));

  return {
    listOfTriggers,
    totalPages: payload.totalPages ?? 0,
    totalCount: payload.totalCount ?? 0,
    currentPage: pageNumber,
  };
};

export const deleteTriggerById = async (triggerId: any) => {
  try {
    const response = await axiosInstance().delete('/trigger/deleteTrigger', {
      data: { triggerId },
    });
    return response?.data?.data;
  } catch (error) {
    console.error('Error deleting trigger:', error);
    throw new Error('Failed to delete trigger');
  }
};

export const fetchTriggerDetails = async (id: string) => {
  try {
    const response = await axiosInstance().get(`/trigger/getTriggerDetails?triggerId=${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching trigger data:', error);
    throw error;
  }
};

export const changeStatus = async (triggerId: string, status: string) => {
  try {
    const response = await axiosInstance().post('/trigger/changeStatus', { triggerId, status });
    return response?.data?.data;
  } catch (error) {
    console.error('Error changing status:', error);
    throw new Error('Failed to change status');
  }
};

export const fetchUserOptions = async (assetIds: string[]): Promise<any[]> => {
  try {
    const response = await axiosInstance().post('/trigger/getUserOptions', { assetIds });
    return response.data;
  } catch (error) {
    console.error('Error fetching user options:', error);
    return [];
  }
};

export const fetchTaskTemplateOptions = async (query: string): Promise<any[]> => {
  try {
    const response = await axiosInstance().get(
      `/taskManagement/getListOfTaskTemplateAutocomplete?taskTemplate=${query}`
    );
    return Array.isArray(response?.data?.data) ? response?.data?.data : [];
  } catch (error) {
    console.error('Error fetching task options:', error);
    return [];
  }
};

export const fetchAssetOptions = async (userGroupIds: string[]): Promise<any[]> => {
  try {
    const response = await axiosInstance().post('/trigger/getAssetOptions', { userGroupIds });
    return response.data;
  } catch (error) {
    console.error('Error fetching asset options:', error);
    return [];
  }
};

export const fetchTriggersByTaskTemplateId = async (taskTemplateId: string) => {
  try {
    const response = await axiosInstance().post('/trigger/getTriggersByTaskTemplateId', {
      taskTemplateId,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching triggers by Task Template ID:', error);
    return [];
  }
};

export const fetchPlantsByUserAutocomplete = async (plantData: PlantListAutoCompleteProps) => {
  try {
    const workspaceId = plantData.workspaceId;
    // const plant = plantData.plant;

    const { data } = await axiosInstance().post(
      `/plants/fetchPlantListByWorkspaceId?workspaceId=${workspaceId}`
    );
    return data?.body;
  } catch (err) {
    console.error(err);
  }
};

export const fetchAssetsByUserGroupId = async (selectedClient: string) => {
  try {
    const { data } = await axiosInstance().post(`/store/getSelectedClientAssets/${selectedClient}`);
    return data?.data;
  } catch (err) {
    console.error('ERROR | fetchAssetsByUserGroupId | ', err);
  }
};

export const getAssetsByAssetQuery = async (assetQuery: string): Promise<any[]> => {
  try {
    const { data } = await axiosInstance().post(
      `/plants/getListOfPlantsForAdministrativeUser`,
      { plantName: assetQuery } // Sending in the request body
    );
    return data?.data;
  } catch (err) {
    console.error('ERROR | getAssetsByAssetQuery | ', err);
    throw err;
  }
};

export const getUsersFromUserGroupIds = async (
  name?: string,
  userGroupId?: string
): Promise<any[]> => {
  try {
    const response = await axiosInstance().post('/newUsers/getUsersAutoComplete', {
      name: name,
      userGroupId: userGroupId,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching users from user group IDs:', error);
    return [];
  }
};

const assignMethodOptions = {
  SKILLS_BASED: 'Skill Based',
  SHIFT_BASED: 'Shift Based',
  EMERGENCY_BASED: 'Emergency Based',
  SCHEDULED_BASED: 'Scheduled Based',
  NON_ASSIGNED_TASK: 'Un-Assigned',
  FIXED_USER: 'Fixed User',
};

export const fetchAssignMethods = async () => {
  try {
    // const response = await axiosInstance().post('/trigger/fetchAssignMethods');
    // if (response.data && response.data.length > 0) {
    //   return response.data;
    // } else {
    return assignMethodOptions;
    // }
  } catch (error) {
    console.error('Error fetching assign methods:', error);
    return assignMethodOptions;
  }
};

export const fetchTaskTemplateData = async (taskId: string) => {
  try {
    const response = await axiosInstance().get(
      `/taskManagement/getTaskTemplateUsingIdForTrigger?taskTemplateId=${taskId}`
    );

    if (!response || response.status !== 200) {
      throw new Error('API request failed');
    }

    return response.data;
  } catch (error) {
    console.error('Fetching task template data failed:', error);
    return null;
  }
};

export const fetchWhatsAppTemplates = async () => {
  try {
    // const response = await axiosInstance().get('/notificationManagement/getListOfWhatsappTemplates');
    const response = await axiosInstance().get(
      `/notificationManagement/getListOfWhatsappTemplates`
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching WhatsApp templates:', error);
    return { data: [] }; // Return an empty array on failure to avoid breaking the UI
  }
};

export const getBooleanFormulaForCondition = async (
  conditionQuery: string,
  SensorType: string,
  assetId?: string[]
): Promise<any> => {
  try {
    const response = await axiosInstance().post('/formula/getFormulasAutoComplete', {
      type: SensorType,
      query: conditionQuery,
      assetId,
      // assetIds,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching boolean formula for condition:', error);
    throw new Error('Failed to fetch boolean formula for condition');
  }
};

export const addObsoleteComponentToTrigger = async (data: any) => {
  try {
    const response = await axiosInstance().post(`/trigger/saveObsoleteTaskDetails`, {
      ...data,
    });

    return response?.data;
  } catch (error) {
    console.error('Error adding component to trigger:', error);
    throw new Error('Failed to add component to trigger.');
  }
};

//updateObsoleteComponentInTrigger
export const updateObsoleteComponentInTrigger = async (data: any) => {
  try {
    const response = await axiosInstance().post(`/trigger/updateObsoleteTaskDetails`, {
      ...data,
    });

    return response?.data;
  } catch (error) {
    console.error('Error adding component to trigger:', error);
    throw new Error('Failed to add component to trigger.');
  }
};

export const getTriggerComponentsByTriggerId = async (triggerId: string) => {
  try {
    const response = await axiosInstance().post('/trigger/getTriggerComponents', { triggerId });

    if (response.status !== 200) {
      throw new Error(`Failed to fetch trigger components: ${response.statusText}`);
    }

    return response.data; // Assuming response contains the trigger components data
  } catch (error) {
    console.error('Error in getTriggerComponentsByTriggerId:', error);
    throw error;
  }
};

export const saveUpdateTriggerTaskComponent = async (triggerComponent: any) => {
  try {
    const response = await axiosInstance().post(
      '/trigger/createOrUpdateTaskComponent',
      triggerComponent
    );
    return response.data?.message || 'Trigger Component saved successfully';
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to save Trigger Component');
  }
};

export const updateTaskComponent = async (triggerComponent: any) => {
  try {
    const response = await axiosInstance().post('/trigger/updateTaskComponent', triggerComponent);
    return response.data?.message || 'Trigger Component updated successfully';
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update Trigger Component');
  }
};

export const saveTriggerCommunicationComponent = async (triggerComponent: any) => {
  try {
    const response = await axiosInstance().post(
      '/trigger/createCommunicationComponent',
      triggerComponent
    );
    return response.data?.message || 'Trigger Component saved successfully';
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to save Trigger Component');
  }
};

export const updateTriggerCommunicationComponent = async (triggerComponent: any) => {
  try {
    const response = await axiosInstance().post(
      '/trigger/updateCommunicationComponent',
      triggerComponent
    );
    return response.data?.message;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update Trigger Component');
  }
};

export const getReportDashboardOptions = async (userIds: string[] = []): Promise<any[]> => {
  try {
    const response = await axiosInstance().post('/newDashboard/getDashboardPagesByUserIds', {
      userIds,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching report dashboard options:', error);
    return [];
  }
};

export const saveTriggerData = async (triggerData: any) => {
  try {
    const response = await axiosInstance().post('/trigger/createTrigger', triggerData);
    return response.data;
  } catch (error: any) {
    console.error('Error saving trigger data:', error);
    const errorMessage =
      error?.response?.data?.message || error.message || 'Fill the Credentials Properly';
    throw new Error(errorMessage);
  }
};

export const editTriggerData = async (triggerData: any) => {
  try {
    const payload = {
      triggerId: triggerData?._id,
      ...triggerData,
    };

    const response = await axiosInstance().post('/trigger/updateTrigger', payload);
    return response.data;
  } catch (error: any) {
    console.error('Error updating trigger:', error);
    const errorMessage = error?.message || 'Fill the Credentials Properly';
    throw new Error(errorMessage);
  }
};

export const getCommunicationComponentDetails = async (communicationComponentId: string) => {
  try {
    const response = await axiosInstance().post('/trigger/getCommunicationComponentDetails', {
      communicationComponentId,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating trigger component:', error);
    throw error;
  }
};

export const getTaskComponentDetails = async (taskComponentId: string) => {
  try {
    const response = await axiosInstance().post('/trigger/getTaskComponentDetails', {
      taskComponentId,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating trigger component:', error);
    throw error;
  }
};

export const getObsoleteComponentDetails = async (obsoleteTaskId: string) => {
  try {
    const response = await axiosInstance().post('/trigger/getObsoleteTaskDetails', {
      obsoleteTaskId,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating trigger component:', error);
    throw error;
  }
};

export const getInsightsComponentDetails = async (insightComponentId: string) => {
  try {
    const response = await axiosInstance().post('/trigger/getInsightComponentDetails', {
      insightComponentId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getEventComponentDetails = async (eventComponentId: string) => {
  try {
    const response = await axiosInstance().post('/trigger/getEventComponentDetails', {
      eventComponentId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCustomNotificationComponentDetails = async (componentId: string) => {
  try {
    const response = await axiosInstance().post('/trigger/getNotificationComponentDetails', {
      componentId,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteTriggerComponent = async (triggerId: string, triggerComponentId: string) => {
  try {
    const response = await axiosInstance().delete('/trigger/deleteTriggerComponent', {
      data: { triggerId, triggerComponentId },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error deleting trigger component:', error);
    throw new Error(error.response?.data?.message || 'Failed to delete trigger component');
  }
};

export const getUsersDataList = async (userQuery: string) => {
  try {
    const { data } = await axiosInstance().post(`/newUsers/usersList`, { name: userQuery });
    return data.data;
  } catch (error) {
    console.error('error | getUsersDataList |', error);
    throw error;
  }
};

export const saveTriggerFormula = async (payload: any) => {
  try {
    const { data } = await axiosInstance().post(`/formula/createOrUpdateFormula`, payload);
    return data.data;
  } catch (err) {
    throw err;
  }
};

export const getTaskManagementEscalation = async (triggerId: string) => {
  try {
    const response = await axiosInstance().post('/taskManagement/getTaskManagementEscalation', {
      triggerId,
    });
    return response.data?.data || [];
  } catch (error) {
    console.error('Error fetching task management escalation:', error);
    return [];
  }
};
